import { useState, useEffect, useCallback } from 'react';
import { Formik, Form, Field } from 'formik';

import {
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Select,
  useTheme,
} from '@mui/material';

import { Edit } from '@mui/icons-material';

import ButtonWithSpinner from '../ButtonWithSpinner';
import ConfirmationModal from '../ConfirmationModal';
import ModalUsers from '../ModalUsers';

import useCompanies from '../../hooks/useCompanies';
import usePlans from '../../hooks/usePlans';
import { useDate } from '../../hooks/useDate';
import moment from 'moment';
import { i18n } from '../../translate/i18n';
import { getStyled } from './styles';
import toastSuccess from '../../toasts/toastSuccess';
import toastError from '../../toasts/toastError';

const formatDueDate = (value) => {
  if (value?.dueDate && moment(value.dueDate).isValid()) {
    return {
      ...value,
      dueDate: moment(value.dueDate).format('YYYY-MM-DD'),
    };
  }
  return value;
};

const initialRecordState = {
  name: '',
  email: '',
  phone: '',
  planId: '',
  status: true,
  dueDate: '',
  recurrence: '',
  password: '',
  document: '',
  paymentMethod: '',
};

export function CompanyForm({
  onSubmit,
  onDelete,
  onCancel,
  initialValue,
  loading,
}) {
  const theme = useTheme();
  const classes = getStyled(theme);
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});

  const { list: listPlans } = usePlans();

  useEffect(() => {
    listPlans().then(setPlans);
  }, [listPlans]);

  const handleSubmit = async (data) => {
    if (data.dueDate === '' || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }
    onSubmit(data);
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  const incrementDueDate = () => {
    const data = { ...initialValue };
    if (data.dueDate && data.dueDate !== '') {
      switch (data.recurrence) {
        case 'MENSAL':
          data.dueDate = moment(data.dueDate)
            .add(1, 'month')
            .format('YYYY-MM-DD');
          break;
        case 'BIMESTRAL':
          data.dueDate = moment(data.dueDate)
            .add(2, 'month')
            .format('YYYY-MM-DD');
          break;
        case 'TRIMESTRAL':
          data.dueDate = moment(data.dueDate)
            .add(3, 'month')
            .format('YYYY-MM-DD');
          break;
        case 'SEMESTRAL':
          data.dueDate = moment(data.dueDate)
            .add(6, 'month')
            .format('YYYY-MM-DD');
          break;
        case 'ANUAL':
          data.dueDate = moment(data.dueDate)
            .add(12, 'month')
            .format('YYYY-MM-DD');
          break;
      }
      onSubmit(data);
    }
  };

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />
      <Formik
        enableReinitialize
        initialValues={{
          ...initialRecordState,
          ...formatDueDate(initialValue),
        }}
        onSubmit={(values, { resetForm }) =>
          setTimeout(() => {
            handleSubmit(values);
            resetForm();
          }, 500)
        }
      >
        {({ values, handleChange }) => (
          <Form>
            <Grid spacing={1} justifyContent='center' container>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Field
                  as={TextField}
                  label={i18n.t('compaies.table.name')}
                  name='name'
                  variant='outlined'
                  sx={{ ...classes.fullWidth, ...theme.custom.textField }}
                  margin='dense'
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Field
                  as={TextField}
                  label={i18n.t('compaies.table.email')}
                  name='email'
                  variant='outlined'
                  sx={{ ...classes.fullWidth, ...theme.custom.textField }}
                  margin='dense'
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Field
                  as={TextField}
                  label={i18n.t('compaies.table.password')}
                  name='password'
                  variant='outlined'
                  sx={{ ...classes.fullWidth, ...theme.custom.textField }}
                  margin='dense'
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Field
                  as={TextField}
                  label={i18n.t('compaies.table.phone')}
                  name='phone'
                  variant='outlined'
                  sx={{ ...classes.fullWidth, ...theme.custom.textField }}
                  margin='dense'
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl margin='dense' variant='outlined' fullWidth>
                  <InputLabel id='plan-selection-label' sx={theme.custom.label}>
                    {i18n.t('compaies.table.plan')}
                  </InputLabel>
                  <Select
                    labelId='plan-selection-label'
                    label={i18n.t('compaies.table.plan')}
                    name='planId'
                    value={values.planId}
                    onChange={handleChange}
                    sx={theme.custom.selectField}
                    MenuProps={theme.custom.menuProps}
                  >
                    {plans.map((plan, key) => (
                      <MenuItem key={key} value={plan.id}>
                        {plan.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                <FormControl margin='dense' variant='outlined' fullWidth>
                  <InputLabel
                    id='status-selection-label'
                    sx={theme.custom.label}
                  >
                    {i18n.t('compaies.table.active')}
                  </InputLabel>
                  <Select
                    labelId='status-selection-label'
                    label={i18n.t('compaies.table.active')}
                    name='status'
                    value={values.status}
                    onChange={handleChange}
                    sx={theme.custom.selectField}
                    MenuProps={theme.custom.menuProps}
                  >
                    <MenuItem value={true}>
                      {i18n.t('compaies.table.yes')}
                    </MenuItem>
                    <MenuItem value={false}>
                      {i18n.t('compaies.table.no')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Field
                  as={TextField}
                  label={i18n.t('compaies.table.document')}
                  name='document'
                  variant='outlined'
                  sx={{ ...classes.fullWidth, ...theme.custom.textField }}
                  margin='dense'
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Field
                  as={TextField}
                  label={i18n.t('compaies.table.dueDate')}
                  type='date'
                  name='dueDate'
                  InputLabelProps={{ shrink: true }}
                  variant='outlined'
                  margin='dense'
                  sx={{ ...classes.fullWidth, ...theme.custom.textField }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl margin='dense' variant='outlined' fullWidth>
                  <InputLabel
                    id='recurrence-selection-label'
                    sx={theme.custom.label}
                  >
                    {i18n.t('compaies.table.recurrence')}
                  </InputLabel>
                  <Select
                    labelId='recurrence-selection-label'
                    label={i18n.t('compaies.table.recurrence')}
                    name='recurrence'
                    value={values.recurrence}
                    onChange={handleChange}
                    sx={theme.custom.selectField}
                    MenuProps={theme.custom.menuProps}
                  >
                    <MenuItem value='MENSAL'>
                      {i18n.t('compaies.table.monthly')}
                    </MenuItem>
                    <MenuItem value='BIMESTRAL'>
                      {i18n.t('compaies.table.bimonthly')}
                    </MenuItem>
                    <MenuItem value='TRIMESTRAL'>
                      {i18n.t('compaies.table.quarterly')}
                    </MenuItem>
                    <MenuItem value='SEMESTRAL'>
                      {i18n.t('compaies.table.semester')}
                    </MenuItem>
                    <MenuItem value='ANUAL'>
                      {i18n.t('compaies.table.yearly')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }} item>
                <Grid justifyContent='flex-end' spacing={1} container>
                  {initialValue.id !== undefined ? (
                    <>
                      <Grid size={{ xs: 6, md: 1 }}>
                        <ButtonWithSpinner
                          sx={{
                            ...classes.fullWidth,
                            ...theme.custom.cancelButton,
                          }}
                          loading={loading}
                          onClick={() => onDelete(initialValue)}
                          variant='outlined'
                        >
                          {i18n.t('compaies.table.delete')}
                        </ButtonWithSpinner>
                      </Grid>
                      <Grid size={{ xs: 6, md: 2 }}>
                        <ButtonWithSpinner
                          sx={{
                            ...classes.fullWidth,
                            ...theme.custom.editButton,
                          }}
                          loading={loading}
                          onClick={incrementDueDate}
                          variant='outlined'
                        >
                          {i18n.t('compaies.table.dueDate')}
                        </ButtonWithSpinner>
                      </Grid>
                    </>
                  ) : null}
                  <Grid size={{ xs: 4, md: 1 }}>
                    <ButtonWithSpinner
                      sx={{
                        ...classes.fullWidth,
                        ...theme.custom.editButton,
                      }}
                      loading={loading}
                      onClick={onCancel}
                      variant='outlined'
                    >
                      {i18n.t('compaies.table.clear')}
                    </ButtonWithSpinner>
                  </Grid>

                  <Grid size={{ xs: 6, md: 1 }}>
                    <ButtonWithSpinner
                      sx={{
                        ...classes.fullWidth,
                        ...theme.custom.confirmButton,
                      }}
                      loading={loading}
                      type='submit'
                      variant='outlined'
                    >
                      {i18n.t('compaies.table.save')}
                    </ButtonWithSpinner>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
}

export function CompaniesManagerGrid({ records, onSelect }) {
  const theme = useTheme();
  const classes = getStyled(theme);
  const { dateToClient, datetimeToClient } = useDate();

  const renderStatus = (row) => (row.status === false ? 'Não' : 'Sim');
  const renderPlan = (row) => (row.planId !== null ? row.plan.name : '-');
  const renderPlanValue = (row) =>
    row.planId !== null
      ? row.plan.amount
        ? row.plan.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 })
        : '00.00'
      : '-';

  const rowStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, 'days');
      if (diff >= 1 && diff <= 5) return { backgroundColor: '#fffead' };
      if (diff <= 0) return { backgroundColor: '#fa8c8c' };
    }
    return {};
  };

  return (
    <Paper sx={classes.tableContainer}>
      <Table sx={classes.fullWidth} padding='none'>
        <TableHead>
          <TableRow>
            <TableCell align='center' style={{ width: '1%' }}>
              #
            </TableCell>
            <TableCell align='left'>{i18n.t('compaies.table.name')}</TableCell>
            <TableCell align='left'>{i18n.t('compaies.table.email')}</TableCell>
            <TableCell align='center'>
              {i18n.t('compaies.table.phone')}
            </TableCell>
            <TableCell align='center'>
              {i18n.t('compaies.table.plan')}
            </TableCell>
            <TableCell align='center'>
              {i18n.t('compaies.table.value')}
            </TableCell>
            <TableCell align='center'>
              {i18n.t('compaies.table.active')}
            </TableCell>
            <TableCell align='center'>
              {i18n.t('compaies.table.createdAt')}
            </TableCell>
            <TableCell align='center'>
              {i18n.t('compaies.table.dueDate')}
            </TableCell>
            <TableCell align='center'>
              {i18n.t('compaies.table.lastLogin')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row, key) => (
            <TableRow style={rowStyle(row)} key={key}>
              <TableCell align='center'>
                <IconButton onClick={() => onSelect(row)}>
                  <Edit />
                </IconButton>
              </TableCell>
              <TableCell align='left'>{row.name || '-'}</TableCell>
              <TableCell align='left'>{row.email || '-'}</TableCell>
              <TableCell align='center'>{row.phone || '-'}</TableCell>
              <TableCell align='center'>{renderPlan(row)}</TableCell>
              <TableCell align='center'>
                {i18n.t('compaies.table.money')} {renderPlanValue(row)}
              </TableCell>
              <TableCell align='center'>{renderStatus(row)}</TableCell>
              <TableCell align='center'>
                {dateToClient(row.createdAt)}
              </TableCell>
              <TableCell align='center'>
                {dateToClient(row.dueDate)}
                <br />
                <span>{row.recurrence}</span>
              </TableCell>
              <TableCell align='center'>
                {datetimeToClient(row.lastLogin)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function CompaniesManager() {
  const theme = useTheme();
  const classes = getStyled(theme);
  const { list, save, update, remove } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState(initialRecordState);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const companyList = await list();
      setRecords(companyList);
    } catch (e) {
      toastError(
        `Não foi possível carregar a lista de registros. ${e.message}`,
        {
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark',
        },
      );
    } finally {
      setLoading(false);
    }
  }, [list]);

  useEffect(() => {
    loadPlans();
  }, []); // eslint-disable-line

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCancel();
      toastSuccess('Operação realizada com sucesso!');
    } catch (e) {
      toastError(`Não foi possível realizar a operação. ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCancel();
      toastSuccess('Operação realizada com sucesso!', {
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'dark',
      });
    } catch (e) {
      toastError(`Não foi possível realizar a operação. ${e.message}`, {
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'dark',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setRecord(initialRecordState);
  };

  const handleSelect = (data) => {
    setRecord({
      id: data.id,
      name: data.name || '',
      phone: data.phone || '',
      email: data.email || '',
      planId: data.planId || '',
      status: data.status === false ? false : true,
      dueDate: data.dueDate || '',
      recurrence: data.recurrence || '',
      password: '',
      document: data.document || '',
      paymentMethod: data.paymentMethod || '',
    });
  };

  return (
    <Paper sx={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid size={{ xs: 12 }}>
          <CompanyForm
            initialValue={record}
            onDelete={() => setShowConfirmDialog(true)}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <CompaniesManagerGrid records={records} onSelect={handleSelect} />
        </Grid>
      </Grid>
      <ConfirmationModal
        title='Exclusão de Registro'
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleDelete}
      >
        Deseja realmente excluir esse registro?
      </ConfirmationModal>
    </Paper>
  );
}
